import * as React from 'react';
import FormSectionHistoryAcceptor from 'Components/FormSectionHistoryAcceptor';
import { IFormContext, withFormContext } from 'Containers/FormContextHandler';
import InputHandler from 'Components/InputHandler';
import FormRow from 'Components/FormRow';
import { styled } from '@mui/system';
import { ReferenceText } from 'Routes/Diagnosis/utils/styled';
import FormSection from 'Components/FormSection';
import colors from '../../../../../../config/theme/colors';
import {
  calculateAlzheimerDiagnosticCriteriaA,
  calculateAlzheimerDiagnosticCriteriaB,
  calculateAlzheimerDiagnosticCriteriaC,
  calculateAlzheimerDiagnosticCriteriaD,
  calculateAlzheimerDiagnosticCriteriaE,
  calculateAlzheimerDiagnosticCriteriaF,
  interpretAlzheimerDiagnosticCriteriaABCD,
} from 'Routes/Diagnosis/utils/Alzheimer';
import { partialDateToValue, sortPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import Unit from 'Components/Unit';
import CDRDialog from './CDRDialog';
import InfoPopover from 'Components/InfoPopover';
import { Container, Item } from 'Components/Grid';
import InformationIcon from 'Components/InfoIcon';
import ExaminationDialog from './ExaminationDialog';
import { abnormalFlagsWithDescription } from 'Routes/Examination/utils';
import { useAppSelector } from 'Store/index';

const GroupStyle = styled('div')({
  backgroundColor: colors.lightestGray,
  padding: '2rem 1rem 2rem 1rem',
  margin: '2rem -1rem 4rem -1rem',
});

const groupElement = (elem?: React.JSX.Element): React.JSX.Element => <GroupStyle>{elem}</GroupStyle>;

const InlineDiv = styled('div')({
  display: 'inline-flex',
});

const SubHeader = styled('div')({
  margin: '1rem 0 2rem 0',
});

const ExaminationRowContent = styled('div')({
  marginBottom: '2rem',
  '* > &:last-of-type': {
    marginBottom: 0,
  },
});

const StyledTable = styled('table')({
  '& td:first-of-type': {
    color: colors.tertiaryText,
    paddingRight: '1rem',
    whiteSpace: 'nowrap',
  },
  '& td:nth-of-type(2)': {
    color: colors.primaryText,
  },
});
const StyledTbody = styled('tbody')({});
const StyledTr = styled('tr')({});
const StyledTd = styled('td')({
  verticalAlign: 'top',
});

const interpretationHeaderStyle: React.CSSProperties = {
  color: colors.primaryText,
  margin: '2rem 0 2rem 0',
  textTransform: 'uppercase',
};

const InterpretationValue = styled('div')({ fontWeight: 600, margin: '2rem 0 2rem 0' });

const Examination = ({ title, examination, editing, view, fm }: IExaminationRow) => {
  if (!examination) {
    if (title === 'Li-FosTau/Li-BAm42') return fm('diagnosis.alzheimer.ratioNotCalculable');
    return <ExaminationDialog codeName={title} editing={editing} view={view} fm={fm} />;
  }
  const fields = ['valueReferenceRange', 'abnormalFlags'].filter(field => Object.keys(examination).includes(field)) as (
    | 'valueReferenceRange'
    | 'abnormalFlags'
  )[];
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <InlineDiv>
        <Unit unit={examination.units} fontWeight="bold">
          {examination.value ?? '-'}
        </Unit>
      </InlineDiv>
      {!examination.units && examination.date && <InlineDiv style={{ padding: '0 1rem' }} />}
      {examination.date && <InlineDiv>{`(${formatPartialDate(examination.date)})`}</InlineDiv>}
      {fields.length > 0 && (
        <InlineDiv style={{ padding: '0 1rem' }}>
          <InformationIcon
            info={
              <StyledTable>
                <StyledTbody>
                  {fields.map((f, i) => (
                    <StyledTr key={i}>
                      <StyledTd>{fm(`examination.${f}`)}</StyledTd>
                      <StyledTd>
                        {examination[f] ?? '-'}
                        {f === 'abnormalFlags' &&
                        examination[f] &&
                        abnormalFlagsWithDescription.includes(examination[f])
                          ? `: ${fm(`examination.abnormalFlagsDescription.${examination[f]}`)}`
                          : ''}
                      </StyledTd>
                    </StyledTr>
                  ))}
                </StyledTbody>
              </StyledTable>
            }
          />
        </InlineDiv>
      )}
    </div>
  );
};

const ExaminationRow = ({ title, examination, editing, view, fm }: IExaminationRow) => (
  <ExaminationRowContent>
    <FormRow
      title={`- ${title}`}
      formatTitle={false}
      headerWidth={6}
      headerStyle={{ color: colors.primaryText }}
      bottomMargin={false}
    >
      <Examination title={title} examination={examination} editing={editing} view={view} fm={fm} />
    </FormRow>
  </ExaminationRowContent>
);

interface IExaminationRow {
  title: string;
  examination: IExamination | undefined;
  editing: IFormContext<IAlzheimer>['editing'];
  view: IFormContext<IAlzheimer>['view'];
  fm: IFormContext<IAlzheimer>['fm'];
}

const AlzheimerForm = ({
  documents = [],
  formData,
  editing,
  view,
  fm,
}: IFormContext<IAlzheimer, IAlzheimer | IExamination>): React.JSX.Element => {
  const isViewing = !editing && !!view?.viewing;
  const docID = editing ?? view?.viewing;

  const geneTestDocs: Array<IGeneTest> =
    useAppSelector((s: IState) => s.documents.sortedAndMergedDocuments)?.filter(doc => doc._type === 'geneTest') || [];
  const geneTestsWithApoe = geneTestDocs.filter(doc => doc?.gene === 'apoe' || doc?.genesIncluded?.includes('apoe'));
  const [latestGeneTestWithApoe, setLatestGeneTestWithApoe] = React.useState<IGeneTest | undefined>(undefined);

  const commonFormSectionProps = {
    formData,
    documentID: docID,
    documents,
    optionFormatter: (s: string): string | React.JSX.Element =>
      Array.isArray(s) ? (
        <React.Fragment>
          {s.map((s2, i) => (
            <div key={`${s2}${i}`}>{fm(`diagnosis.alzheimer.opts.${s2}`)}</div>
          ))}
        </React.Fragment>
      ) : (
        fm(`diagnosis.alzheimer.opts.${s}`)
      ),
    hideCopyButton: isViewing,
  };
  const dialogProps = { editing, view, fm };

  const prefix = 'diagnosis.alzheimer.';

  const optFormatter = (id: string | number | boolean) => <span>{fm(prefix + `opts.${id}`)}</span>;

  const a = calculateAlzheimerDiagnosticCriteriaA(formData.document);
  const b = calculateAlzheimerDiagnosticCriteriaB(formData.document);
  const c = calculateAlzheimerDiagnosticCriteriaC(formData.document);
  const d = calculateAlzheimerDiagnosticCriteriaD(formData.document);
  const e = calculateAlzheimerDiagnosticCriteriaE(formData.document);
  const f = calculateAlzheimerDiagnosticCriteriaF(formData.document);

  const examinations: IExamination[] = documents
    .filter(d => d._type === 'examination' && sortPartialDate(d.date, formData.document.date) <= 0)
    .sort((a, b) => b._cdate - a._cdate)
    .sort((a, b) => sortPartialDate(b.date, a.date)) as IExamination[];

  const liBAm42 = examinations.find(d => d.codeName === 'Li-BAm42');
  const liBAm40 = examinations.find(d => d.codeName === 'Li-BAm40');
  const liBA42_40Ratio = examinations.find(d => d.codeName === 'Li-BA42/40'); // --> calculated if not found
  const liFosTau = examinations.find(d => d.codeName === 'Li-FosTau');
  const pNfl = examinations.find(d => d.codeName === 'P-NFL');
  const liTau = examinations.find(d => d.codeName === 'Li-Tau');
  const pTau217Serum = examinations.find(d => d.codeName === 'P-Tau-217');
  // liFosTau_BAm42 --> calculated at all times

  const apoERiskLevels = {
    '2/2': 'lowOrEvenProtectiveRisk',
    '2/3': 'lowRisk',
    '2/4': 'moderateOrSlightlyElevatedRisk',
    '3/3': 'neutralOrAverageRisk',
    '3/4': 'elevatedRisk',
    '4/4': 'veryHighRisk',
    unknown: 'unknown',
  };

  const apoERiskLevelExists =
    formData.document.supportingFactors?.apoEGenotype &&
    formData.document.supportingFactors?.apoEGenotype !== 'unknown';

  React.useEffect(() => {
    if (formData.document.date) {
      setLatestGeneTestWithApoe(
        geneTestsWithApoe.find(gt => partialDateToValue(gt.date) <= partialDateToValue(formData.document.date)),
      );
    }
  }, [formData.document.date]);

  return (
    <React.Fragment>
      <FormRow title="general.date">
        <InputHandler
          type="PartialDate"
          editing={!isViewing}
          name="date"
          formData={formData}
          dateDefault="now"
          isNotCancellable={true}
        />
      </FormRow>
      {/** A */}
      <FormSectionHistoryAcceptor
        name="functionalImpairment"
        header={prefix + 'functionalImpairment.title'}
        {...commonFormSectionProps}
      >
        {{
          declinedForSixMonths: {
            element: (
              <FormRow title={prefix + 'functionalImpairment.declinedForSixMonths'} headerWidth={6}>
                <InputHandler
                  name="functionalImpairment.declinedForSixMonths"
                  type="RadioButtonRow"
                  editing={!isViewing}
                  formData={formData}
                  options={['yes', 'no']}
                  optionFormatter={optFormatter}
                  height={3.5}
                  width={9}
                />
              </FormRow>
            ),
          },
          cdrGroup: {
            group: {
              groupElement: groupElement,
              children: {
                cdr: {
                  element: (
                    <FormRow
                      title={`- ${fm('cognition.cdr.cdrClass')}`}
                      formatTitle={false}
                      headerWidth={6}
                      headerStyle={{ color: colors.primaryText }}
                    >
                      <div style={{ paddingRight: '2rem' }}>
                        <CDRDialog {...{ documents, formData, editing, view, fm }} />
                      </div>
                    </FormRow>
                  ),
                },
              },
            },
          },
        }}
      </FormSectionHistoryAcceptor>
      {/** B */}
      <FormSectionHistoryAcceptor
        name="cognitiveImpairment"
        header={prefix + 'cognitiveImpairment.title'}
        {...commonFormSectionProps}
      >
        {{
          cognitiveImpairmentHeader: {
            header: <SubHeader>{fm(prefix + 'cognitiveImpairment.subHeader')}</SubHeader>,
          },
          ...['amnestic', 'visualProcessing', 'language', 'atypical'].reduce(
            (acc, field) => {
              acc[field] = {
                element: (
                  <FormRow title={prefix + `cognitiveImpairment.${field}`} headerWidth={6}>
                    <InputHandler
                      name={`cognitiveImpairment.${field}`}
                      type="RadioButtonRow"
                      editing={!isViewing}
                      formData={formData}
                      options={['yes', 'no']}
                      optionFormatter={optFormatter}
                      height={3.5}
                      width={9}
                    />
                  </FormRow>
                ),
              };
              return acc;
            },
            {} as Record<string, { element: React.JSX.Element }>,
          ),
        }}
      </FormSectionHistoryAcceptor>
      {/** C */}
      <FormSectionHistoryAcceptor
        name="amyloidMarkers"
        header={prefix + 'amyloidMarkers.title'}
        {...commonFormSectionProps}
      >
        {{
          reducedBAmyloidInCSF: {
            element: (
              <FormRow title={prefix + 'amyloidMarkers.reducedBAmyloidInCSF'} headerWidth={6}>
                <InputHandler
                  name="amyloidMarkers.reducedBAmyloidInCSF"
                  type="RadioButtonRow"
                  editing={!isViewing}
                  formData={formData}
                  options={['yes', 'no', 'notDone']}
                  optionFormatter={optFormatter}
                  height={3.5}
                  width={9}
                />
              </FormRow>
            ),
          },
          examinationsGroup: {
            group: {
              groupElement: groupElement,
              children: {
                examinations: {
                  element: (
                    <React.Fragment>
                      <ExaminationRow title="Li-BAm42" examination={liBAm42} {...dialogProps} />
                      <ExaminationRow title="Li-BAm40" examination={liBAm40} {...dialogProps} />
                      <ExaminationRow
                        title="Li-BA42/40"
                        examination={
                          liBA42_40Ratio
                            ? liBA42_40Ratio
                            : (liBAm42?.value || liBAm42?.value === 0) && (liBAm40?.value || liBAm40?.value === 0)
                              ? ({
                                  value: (Number(liBAm42.value) / Number(liBAm40.value)).toFixed(3),
                                } as IExamination)
                              : undefined
                        }
                        {...dialogProps}
                      />
                    </React.Fragment>
                  ),
                },
              },
            },
          },
          positiveAmyloidPET: {
            element: (
              <FormRow title={prefix + 'amyloidMarkers.positiveAmyloidPET'} headerWidth={6}>
                <InputHandler
                  name="amyloidMarkers.positiveAmyloidPET"
                  type="RadioButtonRow"
                  editing={!isViewing}
                  formData={formData}
                  options={['yes', 'no', 'notDone']}
                  optionFormatter={optFormatter}
                  height={3.5}
                  width={9}
                />
              </FormRow>
            ),
          },
        }}
      </FormSectionHistoryAcceptor>
      {/** D */}
      <FormSectionHistoryAcceptor name="tauChanges" header={prefix + 'tauChanges.title'} {...commonFormSectionProps}>
        {{
          elevatedFosTau: {
            element: (
              <FormRow title={prefix + 'tauChanges.elevatedFosTau'} headerWidth={6}>
                <InputHandler
                  name="tauChanges.elevatedFosTau"
                  type="RadioButtonRow"
                  editing={!isViewing}
                  formData={formData}
                  options={['yes', 'no', 'notDone']}
                  optionFormatter={optFormatter}
                  height={3.5}
                  width={9}
                />
              </FormRow>
            ),
          },
          examinationsGroup: {
            group: {
              groupElement: groupElement,
              children: {
                examinations: {
                  element: <ExaminationRow title="Li-FosTau" examination={liFosTau} {...dialogProps} />,
                },
              },
            },
          },
        }}
      </FormSectionHistoryAcceptor>
      {/** E */}
      <FormSectionHistoryAcceptor
        name="supportingFactors"
        header={prefix + 'supportingFactors.title'}
        {...commonFormSectionProps}
      >
        {{
          familyHistory: {
            element: (
              <React.Fragment>
                <FormRow title={prefix + 'supportingFactors.familyHistory.title'} headerWidth={6}>
                  <InputHandler
                    name="familyHistory"
                    type="RadioButtonRow"
                    editing={!isViewing}
                    formData={{
                      onChange: (values: TOnChangeValues) => {
                        formData.onChange?.({
                          supportingFactors: { ...formData.document?.supportingFactors, ...values },
                        });
                      },
                      document: {
                        familyHistory: formData.document.supportingFactors?.familyHistory,
                        familyHistoryDetails: formData.document.supportingFactors?.familyHistoryDetails,
                      },
                    }}
                    preset="yesNoUnknown"
                    height={3.5}
                    width={9}
                    dependentFieldsList={() => ['familyHistoryDetails']}
                    dependentFieldsRemovalWarning
                  />
                </FormRow>
                <FormRow headerStyle={{ display: 'none' }}>
                  <></>
                </FormRow>
              </React.Fragment>
            ),
          },
          familyHistoryDetails: {
            element: (
              <React.Fragment>
                <FormRow title={''} formatTitle={false} headerWidth={6}>
                  <InputHandler
                    name="supportingFactors.familyHistoryDetails"
                    type="Checkbox"
                    editing={!isViewing}
                    formData={formData}
                    options={['mother', 'father', 'sister', 'brother']}
                    optionFormatter={optFormatter}
                  />
                </FormRow>
                <FormRow headerStyle={{ display: 'none' }}>
                  <></>
                </FormRow>
              </React.Fragment>
            ),
            condition: formData.document?.supportingFactors?.familyHistory === 'yes',
          },
          apoEGenotype: {
            element: (
              <React.Fragment>
                <FormRow
                  title={prefix + 'supportingFactors.apoEGenotype.title'}
                  headerWidth={6}
                  bottomMargin={!apoERiskLevelExists}
                >
                  <Container>
                    <Item xs={7}>
                      <InputHandler
                        name="supportingFactors.apoEGenotype"
                        type="Radio"
                        editing={!isViewing}
                        formData={formData}
                        options={['2/2', '2/3', '2/4', '3/3', '3/4', '4/4', 'unknown']}
                        optionFormatter={optFormatter}
                      />
                    </Item>
                    {latestGeneTestWithApoe ? (
                      <Item xs={5} style={{ color: colors.tertiaryText }}>
                        <div>{fm('geneTest.shortTitle') + ` (${formatPartialDate(latestGeneTestWithApoe.date)})`}</div>
                        <div style={{ fontWeight: 600 }}>
                          {fm(`geneTest.opts.${latestGeneTestWithApoe.geneTestType}`)}
                        </div>
                        <div style={{ fontWeight: 600 }}>
                          {fm(`geneTest.opts.genoType.${latestGeneTestWithApoe.genoType}`)}
                        </div>
                      </Item>
                    ) : null}
                  </Container>
                </FormRow>
                {apoERiskLevelExists && (
                  <FormRow title={''} formatTitle={false} headerWidth={6}>
                    <div style={{ fontWeight: 600, margin: '1rem 0 1rem 0' }}>
                      {fm(
                        prefix +
                          `supportingFactors.apoEGenotype.${
                            apoERiskLevels[formData.document.supportingFactors!.apoEGenotype!]
                          }`,
                      )}
                    </div>
                  </FormRow>
                )}
                <FormRow headerStyle={{ display: 'none' }}>
                  <></>
                </FormRow>
              </React.Fragment>
            ),
          },
          ...['hippocampalAtrophy', 'abnormalFDGPET', 'elevatedNeurofilamentOrTau'].reduce(
            (acc, field) => {
              acc[field] = {
                element: (
                  <FormRow title={prefix + `supportingFactors.${field}`} headerWidth={6}>
                    <InputHandler
                      name={`supportingFactors.${field}`}
                      type="RadioButtonRow"
                      editing={!isViewing}
                      formData={formData}
                      preset="yesNoUnknown"
                      height={3.5}
                      width={9}
                    />
                  </FormRow>
                ),
              };
              return acc;
            },
            {} as Record<string, { element: React.JSX.Element }>,
          ),
          examinationsGroup1: {
            group: {
              groupElement: groupElement,
              children: {
                examinations1: {
                  element: (
                    <React.Fragment>
                      <ExaminationRow title="P-NFL" examination={pNfl} {...dialogProps} />
                      <ExaminationRow title="Li-Tau" examination={liTau} {...dialogProps} />
                    </React.Fragment>
                  ),
                },
              },
            },
          },
          otherTau: {
            element: (
              <FormRow title={prefix + 'supportingFactors.otherTau'} headerWidth={6}>
                <InputHandler
                  name="supportingFactors.otherTau"
                  type="RadioButtonRow"
                  editing={!isViewing}
                  formData={formData}
                  preset="yesNoUnknown"
                  height={3.5}
                  width={9}
                />
              </FormRow>
            ),
          },
          examinationsGroup2: {
            group: {
              groupElement: groupElement,
              children: {
                examinations2: {
                  element: (
                    <React.Fragment>
                      <ExaminationRow title="P-Tau-217" examination={pTau217Serum} {...dialogProps} />
                      <ExaminationRow
                        title="Li-FosTau/Li-BAm42"
                        examination={
                          (liFosTau?.value || liFosTau?.value === 0) && liBAm42?.value
                            ? ({
                                value: (Number(liFosTau.value) / Number(liBAm42.value)).toFixed(3),
                              } as IExamination)
                            : undefined
                        }
                        {...dialogProps}
                      />
                    </React.Fragment>
                  ),
                },
              },
            },
          },
        }}
      </FormSectionHistoryAcceptor>
      {/** F */}
      <FormSectionHistoryAcceptor name="redFlags" header={prefix + 'redFlags.title'} {...commonFormSectionProps}>
        {{
          ghostHeader: {
            header: <div style={{ height: '1rem' }} />,
          },
          ...[
            'untreatedSleepDisorder',
            'untreatedPsychiatricDisorder',
            'substanceUse',
            'cognitiveAffectingMedications',
            'lowCognitiveBaseline',
            'inflammatoryOrMalignancyFindings',
          ].reduce(
            (acc, field) => {
              acc[field] = {
                element: (
                  <FormRow title={prefix + `redFlags.${field}`} headerWidth={6}>
                    <InputHandler
                      name={`redFlags.${field}`}
                      type="RadioButtonRow"
                      editing={!isViewing}
                      formData={formData}
                      preset="yesNoUnknown"
                      height={3.5}
                      width={9}
                    />
                  </FormRow>
                ),
              };
              return acc;
            },
            {} as Record<string, { element: React.JSX.Element }>,
          ),
        }}
      </FormSectionHistoryAcceptor>
      <FormSection>
        <FormRow
          title={prefix + 'interpretation.title'}
          headerWidth={5}
          headerStyle={interpretationHeaderStyle}
          bottomMargin={false}
        >
          <InterpretationValue>
            {a && b && interpretAlzheimerDiagnosticCriteriaABCD(a, b, c, d) ? (
              fm(prefix + `interpretation.${interpretAlzheimerDiagnosticCriteriaABCD(a, b, c, d)}`)
            ) : (
              <InfoPopover text={prefix + 'notFilled'} color="primary" />
            )}
          </InterpretationValue>
        </FormRow>
        <FormRow
          title={prefix + 'supportingFactors.title'}
          headerWidth={5}
          headerStyle={interpretationHeaderStyle}
          bottomMargin={false}
        >
          <InterpretationValue>{`${e ?? '-'} / 6`}</InterpretationValue>
        </FormRow>
        <FormRow title={prefix + 'redFlags.titleShort'} headerWidth={5} headerStyle={interpretationHeaderStyle}>
          <React.Fragment>
            <InterpretationValue>{`${f ?? '-'} / 6`}</InterpretationValue>
            {!!f && (
              <InterpretationValue>
                {f === 1 ? fm(prefix + 'interpretation.oneRedFlag') : fm(prefix + 'interpretation.twoOrMoreRedFlags')}
              </InterpretationValue>
            )}
          </React.Fragment>
        </FormRow>
      </FormSection>
      <ReferenceText>
        Dubois, B., Hampel, H., Aisen, P. S., Cummings, J., Jack, C. R., Lapucci, C., ... & Scheltens, P. (2024).
        Alzheimer disease as a clinical-biological construct: An International Working Group recommendation. JAMA
        Neurology, Advance online publication. https://doi.org/10.1001/jamaneurol.2024.3356
      </ReferenceText>
    </React.Fragment>
  );
};

export default withFormContext(AlzheimerForm);
